// Config panel for the "output_state_changed" trigger block
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { qbusApi } from '../../../services/apiClient';

const OutputStateChangedConfig = ({ config, locationId, onChange, onSummaryChange }) => {
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    setLoading(true);
    qbusApi.getOutputsForLocation(locationId)
      .then((res) => setOutputs(res.data.outputs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locationId]);

  const handleOutputChange = (value) => {
    // value can be a string (typed) or an output object (selected)
    const updated = { ...config };
    if (typeof value === 'object' && value !== null) {
      updated.outputId = value.id;
      updated.outputName = value.friendlyName || value.name || value.output_id;
    } else {
      updated.outputId = null;
      updated.outputName = value || '';
    }
    onChange(updated);
    onSummaryChange(
      updated.expectedValue !== undefined && updated.expectedValue !== ''
        ? `${updated.outputName || '?'} → ${updated.expectedValue}`
        : updated.outputName || '?'
    );
  };

  const handleExpectedValueChange = (value) => {
    const updated = { ...config, expectedValue: value || undefined };
    onChange(updated);
    const name = config.outputName || `#${config.outputId || '?'}`;
    onSummaryChange(
      value ? `${name} → ${value}` : name
    );
  };

  if (loading) return <CircularProgress size={24} />;

  // Build options list from loaded outputs
  const options = outputs.map((o) => ({
    ...o,
    label: `${o.friendlyName || o.name || o.output_id}`,
  }));

  // Resolve current value for the Autocomplete
  const currentValue = config.outputId
    ? options.find((o) => o.id === config.outputId) || null
    : null;
  const inputValue = config.outputName || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Fires when the selected output changes state.
      </Typography>

      <Autocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : (option.friendlyName || option.name || option.output_id || '')
        }
        value={currentValue}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue) => {
          if (!currentValue || newInputValue !== (currentValue.friendlyName || currentValue.name || currentValue.output_id)) {
            handleOutputChange(newInputValue);
          }
        }}
        onChange={(_e, newValue) => {
          handleOutputChange(newValue);
        }}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.friendlyName || option.name || option.output_id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.output_id} &middot; {option.outputType?.friendlyName || option.function_block_type || ''}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Output"
            required
            helperText="Type an output name or select from the list"
          />
        )}
      />

      <TextField
        size="small"
        label="Expected Value (optional)"
        value={config.expectedValue ?? ''}
        onChange={(e) => handleExpectedValueChange(e.target.value)}
        helperText="Leave empty to trigger on any change, or specify a value"
      />
    </Box>
  );
};

export default OutputStateChangedConfig;
